import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';

// Components
import Login from './components/Login';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { AlertManager } from './components/CustomAlert';
import AppInitializer from './components/AppInitializer';
import { getSectionPrivileges, getFirstAllowedPath } from './utils/positionsApi';
import * as Pages from './routes/lazyPages';
import { useSelector } from 'react-redux';
import AttendanceSheetForm from './pages/Students/AttendanceSheetForm';
import { StudentEnrollmentFormProvider } from './pages/Students/StudentEnrollmentFormContext';
import StudentAcknowledgmentForm from './pages/Students/StudentAcknowledgmentForm';
import StudentEmergencyContactForm from './pages/Students/StudentEmergencyContactForm';
import UniformSizeForm from './pages/Students/UniformSizeForm';
import StudentIdForm from './pages/Students/StudentIdForm';
import MedicalEvaluationForm from './pages/Students/MedicalEvaluationForm';
import BackgroundCheckForm from './pages/Students/BackgroundCheckForm';
import EnrollmentAgreementForm from './pages/Students/EnrollmentAgreementForm';
import ConsentReleaseInfoForm from './pages/Students/ConsentReleaseInfoForm';
import MediaReleaseForm from './pages/Students/MediaReleaseForm';
import CovidVaccinationAttestationForm from './pages/Students/CovidVaccinationAttestationForm';
import CellPhonePolicyForm from './pages/Students/CellPhonePolicyForm';
import MedicalSuppliesAcknowledgmentForm from './pages/Students/MedicalSuppliesAcknowledgmentForm';

const DASHBOARD_PRIVS = getSectionPrivileges('dashboard');
const SESSIONS_PRIVS = getSectionPrivileges('sessions');
const STUDENTS_PRIVS = getSectionPrivileges('students');
const FINANCE_PRIVS = getSectionPrivileges('finance');
const APPLICATIONS_PRIVS = getSectionPrivileges('applications');
const STAFF_PRIVS = getSectionPrivileges('staff');

// Redirect to first allowed route (or login if not authenticated)
const DefaultRedirect = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getFirstAllowedPath(user)} replace />;
};

// Wrapper component to extract id parameter
const MedicalEvaluationFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <MedicalEvaluationForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter
const BackgroundCheckFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <BackgroundCheckForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for ConsentReleaseInfoForm
const ConsentReleaseInfoFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <ConsentReleaseInfoForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for MediaReleaseForm
const MediaReleaseFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <MediaReleaseForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for CovidVaccinationAttestationForm
const CovidVaccinationAttestationFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <CovidVaccinationAttestationForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for CellPhonePolicyForm
const CellPhonePolicyFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <CellPhonePolicyForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for MedicalSuppliesAcknowledgmentForm
const MedicalSuppliesAcknowledgmentFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <MedicalSuppliesAcknowledgmentForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for EnrollmentAgreementForm
const EnrollmentAgreementFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <EnrollmentAgreementForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for StudentAcknowledgmentForm
const StudentAcknowledgmentFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <StudentAcknowledgmentForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for StudentEmergencyContactForm
const StudentEmergencyContactFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <StudentEmergencyContactForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for UniformSizeForm
const UniformSizeFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <UniformSizeForm />
    </StudentEnrollmentFormProvider>
  );
};

// Wrapper component to extract id parameter for StudentIdForm
const StudentIdFormWrapper = () => {
  const { id } = useParams();
  return (
    <StudentEnrollmentFormProvider id={id}>
      <StudentIdForm />
    </StudentEnrollmentFormProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppInitializer>
          <Router basename="/admin">
          {/* <Router> */}
            <AlertManager />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/students/:studentId/attendance-sheet" element={<AttendanceSheetForm />} />
              <Route path="/consent-release-info-form/:id" element={<ConsentReleaseInfoFormWrapper />} />
              
              {/* Protected Routes */}
              <Route element={<AuthenticatedLayout />}>
<Route path="/dashboard" element={
                <ProtectedRoute requiredPermissions={DASHBOARD_PRIVS}>
                    <Pages.Dashboard />
                </ProtectedRoute>
              } />

              <Route path="/activity-log" element={
                <ProtectedRoute requiredPermission="activity_log_read">
                    <Pages.ActivityLog />
                </ProtectedRoute>
              } />

              {/* Sessions and Courses Routes */}
              <Route path="/sessions" element={
                <ProtectedRoute requiredPermissions={SESSIONS_PRIVS}>
                    <Pages.Sessions />
                </ProtectedRoute>
              } />
              
              <Route path="/courses" element={
                <ProtectedRoute requiredPermissions={SESSIONS_PRIVS}>
                    <Pages.Courses />
                </ProtectedRoute>
              } />

              {/* Notifications Routes */}
              <Route path="/notifications/send-announcement" element={
                <ProtectedRoute>
                    <Pages.SendAnnouncement />
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/announcements-list" element={
                <ProtectedRoute>
                    <Pages.AnnouncementsList />
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/send-sms" element={
                <ProtectedRoute>
                    <Pages.SendSMS />
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/all-sms" element={
                <ProtectedRoute>
                    <Pages.AllSMS />
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/sms-chat" element={
                <ProtectedRoute>
                    <Pages.SMSChat />
                </ProtectedRoute>
              } />
              
              <Route path="/notifications/send-password" element={
                <ProtectedRoute>
                    <Pages.SendPassword />
                </ProtectedRoute>
              } />

              {/* Student Routes */}
              <Route path="/students/add" element={
                <ProtectedRoute>
                    <Pages.AddStudentApplication />
                </ProtectedRoute>
              } />
              
              <Route path="/students/list" element={
                <ProtectedRoute>
                    <Pages.StudentList />
                </ProtectedRoute>
              } />
              
              <Route path="/students/assign-session" element={
                <ProtectedRoute requiredPermissions={STUDENTS_PRIVS}>
                    <Pages.AssignSession />
                </ProtectedRoute>
              } />
              
              <Route path="/students/transfer" element={
                <ProtectedRoute requiredPermissions={STUDENTS_PRIVS}>
                    <Pages.StudentTransfer />
                </ProtectedRoute>
              } />

              <Route path="/students/transfer-history" element={
                <ProtectedRoute>
                    <Pages.TransferHistory />
                </ProtectedRoute>
              } />
              
              <Route path="/students/bulk-send-document" element={
                <ProtectedRoute>
                    <Pages.BulkSendDocument />
                </ProtectedRoute>
              } />
              
              <Route path="/students/previous-course" element={
                <ProtectedRoute>
                    <Pages.PreviousCourse />
                </ProtectedRoute>
              } />
              
              <Route path="/students/vouchers" element={
                <ProtectedRoute requiredPermissions={STUDENTS_PRIVS}>
                    <Pages.Vouchers />
                </ProtectedRoute>
              } />

              {/* Instructor Routes */}
              <Route path="/instructors/add" element={
                <ProtectedRoute>
                    <Pages.AddEmployee />
                </ProtectedRoute>
              } />
              
              <Route path="/instructors/all" element={
                <ProtectedRoute>
                    <Pages.AllInstructors />
                </ProtectedRoute>
              } />

              {/* Staff Routes */}
              <Route path="/staff/add" element={
                <ProtectedRoute>
                    <Pages.AddStaff />
                </ProtectedRoute>
              } />
              
              <Route path="/staff/all" element={
                <ProtectedRoute>
                    <Pages.AllStaffs />
                </ProtectedRoute>
              } />

              <Route path="/staff/positions" element={
                <ProtectedRoute requiredPermissions={STAFF_PRIVS}>
                    <Pages.Positions />
                </ProtectedRoute>
              } />

              <Route path="/staff/users" element={
                <ProtectedRoute requiredPermissions={STAFF_PRIVS}>
                    <Pages.UserManagement />
                </ProtectedRoute>
              } />

              <Route path="/staff/career-coaches" element={
                <ProtectedRoute requiredPermissions={STAFF_PRIVS}>
                    <Pages.CareerCoaches />
                </ProtectedRoute>
              } />

              {/* Attendance Routes */}
              <Route path="/attendance/add" element={
                <ProtectedRoute>
                    <Pages.AddAttendance />
                </ProtectedRoute>
              } />
              
              {/* <Route path="/attendance/dashboard" element={
                <ProtectedRoute>
                    <AttendanceDashboard />
                </ProtectedRoute>
              } /> */}
              
              <Route path="/attendance/report" element={
                <ProtectedRoute>
                    <Pages.AttendanceReport />
                </ProtectedRoute>
              } />

              {/* Finance Routes */}
              <Route path="/finance/billing" element={
                <ProtectedRoute requiredPermissions={FINANCE_PRIVS}>
                    <Pages.Billing />
                </ProtectedRoute>
              } />

              {/* Applications Routes */}
              <Route path="/applications/applied-candidates" element={
                <ProtectedRoute requiredPermissions={APPLICATIONS_PRIVS}>
                    <Pages.AllAppliedCandidates />
                </ProtectedRoute>
              } />
              
              <Route path="/applications/all-applied-candidates" element={
                <ProtectedRoute requiredPermissions={APPLICATIONS_PRIVS}>
                    <Pages.AllAppliedCandidates />
                </ProtectedRoute>
              } />

              {/* Profile Management Routes */}
              <Route path="/profile/academic-year" element={
                <ProtectedRoute>
                    <Pages.AcademicYear />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/departments" element={
                <ProtectedRoute>
                    <Pages.Departments />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/semesters" element={
                <ProtectedRoute>
                    <Pages.Semesters />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/designations" element={
                <ProtectedRoute>
                    <Pages.Designations />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/locations" element={
                <ProtectedRoute>
                    <Pages.Locations />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/days-preferred" element={
                <ProtectedRoute>
                    <Pages.DaysPreferred />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/individuals" element={
                <ProtectedRoute>
                    <Pages.Individuals />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/workforces" element={
                <ProtectedRoute>
                    <Pages.Workforces />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/master-data" element={
                <ProtectedRoute>
                    <Pages.MasterData />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/particulars" element={
                <ProtectedRoute>
                    <Pages.Particulars />
                </ProtectedRoute>
              } />

              {/* Update Course & Session Route */}
              <Route path="/update-course-session" element={
                <ProtectedRoute>
                    <Pages.UpdateCourseSession />
                </ProtectedRoute>
              } />
              </Route>

              {/* Public student form routes (no auth shell) */}
              <Route path="/student-acknowledgment/:id" element={<StudentAcknowledgmentFormWrapper />} />
              <Route path="/student-emergency-contact/:id" element={<StudentEmergencyContactFormWrapper />} />
              <Route path="/uniform-size-form/:id" element={<UniformSizeFormWrapper />} />
              <Route path="/student-id-form/:id" element={<StudentIdFormWrapper />} />
              <Route path="/medical-evaluation-form/:id" element={<MedicalEvaluationFormWrapper />} />
              <Route path="/background-check-form/:id" element={<BackgroundCheckFormWrapper />} />
              <Route path="/enrollment-agreement-form/:id" element={<EnrollmentAgreementFormWrapper />} />
              <Route path="/media-release-form/:id" element={<MediaReleaseFormWrapper />} />
              <Route path="/covid-vaccination-attestation-form/:id" element={<CovidVaccinationAttestationFormWrapper />} />
              <Route path="/cell-phone-policy-form/:id" element={<CellPhonePolicyFormWrapper />} />
              <Route path="/medical-supplies-acknowledgment-form/:id" element={<MedicalSuppliesAcknowledgmentFormWrapper />} />

              {/* Default redirect to first allowed route (or login) */}
              <Route path="/" element={<DefaultRedirect />} />
              
              {/* Catch all route */}
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
          </Router>
        </AppInitializer>
      </PersistGate>
    </Provider>
  );
}

export default App;
